import React, { useContext, useEffect, useState } from 'react';
import { DossierOpenSearch, useFormaliteService, useSociete } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/system';
import { Modal, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { DossierActionContext, useDossier } from '@europrocurement/l2d-modules';
import { Switch, UtilisateurSearchList } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { useForm } from 'react-hook-form';
import {
    FormalisteApiApiFormalistesGetCollectionRequest,
    FormalisteJsonldFormalisteRead,
    NotesApiApiNotesPostRequest,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { AnyAction } from '@reduxjs/toolkit';
import { REALM_RESP_FORMALIST, UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useUtilisateur } from '../../../../../../hooks/useUtilisateur';

const AssignDossierModal: React.FunctionComponent = function () {
    const { currentAction, cancelCurrentAction, dossiers } = useContext(DossierActionContext);
    const { getOpenSearchDossiers, notesApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const { updateDossier } = useDossier();
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const { getUserId } = useUtilisateur();
    const societeId = useSociete();
    const keycloakService = UseKeycloakService();

    const [userId, setUserId] = useState<number>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [submitingProcess, setSubmittingProcess] = useState<boolean>(false);
    const [dossier, setDossier] = useState<DossierOpenSearch | undefined>();
    const [selectedUser, setSelectedUser] = useState<UtilisateursJsonldUserworkgroupRead | null>(
        null,
    );
    const [displayAllGroups, setDisplayAllGroups] = useState<boolean>(false);
    const [availableFormalistes, setAvailableFormalistes] = useState<
        FormalisteJsonldFormalisteRead[]
    >([]);

    const isFormalistManager = keycloakService.checkRole(REALM_RESP_FORMALIST);

    // Services API
    const { formalisteApi } = useFormaliteService();

    const { apiErrorMessageSnackbar } = useHandleError();

    useEffect(() => {
        setModalOpen(currentAction === 'assign');
    }, [currentAction]);

    useEffect(() => {
        if (dossiers.length === 1) {
            setDossier(dossiers[0]);
        }
    }, [dossiers]);

    // fetch current user id and user groups when the user is a manager
    useEffect(() => {
        getUserId()
            .then((id) => setUserId(id))
            .catch(() => {});
    }, [getUserId]);

    /**
     * Charge la liste des formalistes disponibles depuis l'API
     */
    useEffect(() => {
        const loadAvailableFormalistes = async () => {
            try {
                const requestParameters: FormalisteApiApiFormalistesGetCollectionRequest = {
                    deleted: false,
                };

                const response = await formalisteApi.apiFormalistesGetCollection(requestParameters);
                setAvailableFormalistes(response.data?.['hydra:member'] || []);
            } catch (error) {
                console.error('Erreur lors du chargement des formalistes:', error);
                enqueueSnackbar('Erreur lors du chargement des formalistes', { variant: 'error' });
                setAvailableFormalistes([]);
            }
        };

        loadAvailableFormalistes();
    }, [formalisteApi, enqueueSnackbar]);

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
        setSubmittingProcess(false);
        cancelCurrentAction();
    };

    const formStructure: FormStructure[] = [
        {
            type: 'textarea',
            name: 'message',
            inputlabel: 'Message',
            inputProps: {
                multiline: true,
                maxRows: Infinity,
            },
            sx: {
                minHeight: '100px',
                padding: '8px 20px',
                border: `1px solid ${palette.grey['300']}`,
                borderRadius: '12px',
                ':hover': {
                    border: `1px solid ${palette.grey['900']}`,
                },
                ':focus-within': {
                    border: `1px solid ${palette.primary.main}`,
                },
                transition: 'all ease-in-out 300ms',
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
            },
        },
    ];

    const formContext = useForm({
        mode: 'onTouched',
    });

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: () => {},
        submitButton: {
            displayed: false,
        },
    };

    const handleUpdateAssign = () => {
        if (!selectedUser) {
            return;
        }
        if (submitingProcess) {
            return;
        }

        setSubmittingProcess(true);

        if (dossier && dossier.id) {
            // Check if a note has to be sent
            let hasNote = false;
            const { message } = formContext.getValues();
            if (message && message.trim().length > 0) {
                hasNote = true;
            }
            const requestPost: NotesApiApiNotesPostRequest = {
                notesEtRappelsJsonldNoteCreate: {
                    dossier: `/formalite/dossiers/${dossier.id}`,
                    description: message,
                    userCreation: userId || undefined,
                    societe: societeId,
                },
            };

            // Multiple promises sent -> 1 for formalist and the other one for the note
            const promisesArray = [
                updateDossier(dossier.id, { formaliste: selectedUser.id }),
                hasNote && notesApi.apiNotesPost(requestPost),
            ];

            Promise.allSettled(promisesArray)
                .then((results) => {
                    const rejectedPromises = results.filter(
                        (result) => result.status === 'rejected',
                    );

                    if (rejectedPromises.length === 0) {
                        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
                        handleCloseModal();
                        enqueueSnackbar(
                            <Typography>
                                Le dossier à bien été assigné à {selectedUser.nom}{' '}
                                {selectedUser.prenom}
                            </Typography>,
                        );
                    } else {
                        rejectedPromises.map((result) => {
                            console.log(result.reason);
                            apiErrorMessageSnackbar(
                                result.reason,
                                "Une erreur est survenue lors de l'assignation de l'utilisateur",
                            );
                            return false;
                        });
                        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
                        setSubmittingProcess(false);
                    }
                })
                .catch((error) => {
                    setSubmittingProcess(false);
                    apiErrorMessageSnackbar(
                        error,
                        "Une erreur est survenue lors de l'assignation de l'utilisateur",
                    );
                });
        } else {
            enqueueSnackbar(
                `Une erreur est survenue lors de la mise à jour du dossier (dossier inexistant)`,
                {
                    variant: 'error',
                },
            );
            setSubmittingProcess(false);
        }
    };

    // const renderUserInfo = (user: UtilisateursGroupesTravailWithGroupLib) =>
    //     `${user.groupetravailLibelle} ${user.service?.libelle && ` - ${user.service?.libelle}`}`;

    const extraSearchBarButton = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '4px',
                gap: '16px',
            }}
        >
            <Switch
                value={displayAllGroups}
                onChange={() => setDisplayAllGroups((prevVal) => !prevVal)}
            />
            <Typography variant="l2dlabel">Afficher tous les groupes</Typography>
        </Box>
    );

    /**
     * Trouve le formaliste actuellement assigné dans la liste des formalistes disponibles
     */
    const getCurrentFormaliste = (): FormalisteJsonldFormalisteRead | null => {
        if (!selectedUser?.nom || !selectedUser?.prenom) return null;

        return (
            availableFormalistes.find((formaliste) => {
                const idMatch =
                    selectedUser?.id && formaliste.id?.toString() === selectedUser?.id.toString();
                const nameMatch =
                    formaliste.prenom === selectedUser?.prenom &&
                    formaliste.nom === selectedUser?.nom;

                return idMatch || nameMatch;
            }) || null
        );
    };

    if (dossier) {
        return (
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="create-modal-title"
                aria-describedby="create-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 710,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: '12px',
                        padding: '30px',
                    }}
                >
                    <IconButton
                        aria-label="close"
                        data-testid="close-button-test"
                        onClick={handleCloseModal}
                        sx={{
                            fontSize: '16px',
                            position: 'absolute',
                            right: '20px',
                            top: '35px',
                            color: palette.grey[500],
                        }}
                    >
                        <FaOptionIcon icon={['fas', 'close']} />
                    </IconButton>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            gap: '24px',
                        }}
                    >
                        <Typography variant="l2dh6">Réassigner le dossier</Typography>
                        <Box sx={{ width: '100%', gap: '8px', display: 'grid' }}>
                            <UtilisateurSearchList
                                users={availableFormalistes}
                                selectedUser={getCurrentFormaliste()}
                                setSelectedUser={setSelectedUser}
                                extraSearchBarButton={
                                    isFormalistManager ? extraSearchBarButton : undefined
                                }
                                listTitle="Contacts"
                            />
                            <Box>
                                <FlexyForm
                                    formStructure={formStructure}
                                    {...formProps}
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                {submitingProcess ? (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            padding: '16px 24px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minWidth: '6rem',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <CircularProgress
                                            color="info"
                                            sx={{
                                                maxHeight: '15px',
                                                maxWidth: '15px',
                                                color: 'white',
                                            }}
                                        />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleUpdateAssign}
                                        sx={{
                                            padding: '16px 24px',
                                            fontSize: '16px',
                                            transition: 'all ease-in-out 300ms',
                                            backgroundColor: selectedUser
                                                ? palette.primary.main
                                                : palette.grey[300],
                                            ':hover': {
                                                backgroundColor: selectedUser
                                                    ? palette.primary.dark
                                                    : palette.grey[300],
                                            },
                                        }}
                                        variant="contained"
                                    >
                                        Partager
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        );
    }
    return null;
};

export default AssignDossierModal;
